import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Chip, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useDispatch, useSelector } from 'react-redux';
import { Swiper, SwiperSlide } from 'swiper/react';
import { M_PER_NEW_01_Schema } from '../M_PER_NEW_01/M_PER_NEW_01_Schema';
import { PermitActions } from '../../../../Reducer/PermitReducer';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import Icon from '../../../../Assets/Icon/CustomIcon';
import { CustomTextAreaBox } from './Inputs';

const WriteCheckListCommon = forwardRef(function WriteCheckListCommon(
  { title, codeListKey, handleChange = () => {}, errors = {} },
  ref
) {
  const dispatch = useDispatch();
  const { baseInfo, codeInfo } = useSelector((state) => state.permit);

  const [chipIndex, setChipIndex] = useState(0);
  // "다음" 버튼을 눌러 지나간 탭만 방문 기록으로 남김
  const [visitedTabs, setVisitedTabs] = useState(new Set([0]));

  const schema =
    (codeListKey && M_PER_NEW_01_Schema.fields.codeInfo[codeListKey]) || {};
  const { nameKey = '', codeKey = '' } = schema;

  const selectedWorkTypeItems = (baseInfo.workTypes || [])
    .map((typeId) =>
      codeInfo?.workTypes?.find((item) => item.workType === typeId)
    )
    .filter(Boolean);

  const currentItem = selectedWorkTypeItems[chipIndex];
  const currentWorkType = currentItem?.workType;
  const currentWorkItemId = currentItem?.workItemId;

  const filteredList =
    codeListKey && codeInfo[codeListKey]
      ? codeInfo[codeListKey].filter(
          (item) => item.workType === currentWorkType
        )
      : [];

  const handleToggle = (code) => {
    const currentList = codeInfo[codeListKey] || [];
    const updatedList = currentList.map((item) =>
      item[codeKey] === code
        ? { ...item, itemCheck: item.itemCheck === 'Y' ? '' : 'Y' }
        : item
    );
    dispatch(PermitActions.setCodeInfo({ [codeListKey]: updatedList }));
  };

  const getCardStyle = (checked) => ({
    cursor: 'pointer',
    borderRadius: '12px',
    backgroundColor: checked ? '#fbfafdff' : '#FFFFFF',
    border: `1.5px solid ${checked ? '#5c6cfaff' : '#E0E0E0'}`,
    padding: '14px 18px',
    marginBottom: '10px',
    boxShadow: checked
      ? '0 3px 8px rgba(153, 151, 158, 0.25)'
      : '0 1px 4px rgba(0,0,0,0.05)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'all 0.25s ease',
  });

  const hasContent = (item) => {
    if (title === '기타 특이사항') {
      const val = baseInfo.etcRemarks?.[item.workItemId];
      return !!val && val.trim() !== '';
    }
    const list = (codeInfo[codeListKey] || []).filter(
      (ci) => ci.workType === item.workType
    );
    return list.some((ci) => ci.itemCheck === 'Y');
  };

  useImperativeHandle(
    ref,
    () => ({
      validateAndNavigate: () => {
        for (let i = 0; i < selectedWorkTypeItems.length; i++) {
          const item = selectedWorkTypeItems[i];
          // 내용이 있거나 "다음"을 눌러 이미 지나간 탭은 건너뜀
          if (hasContent(item) || visitedTabs.has(i)) continue;
          // 처음 지나가는 내용 없는 탭 → 보여주고 방문 기록 추가
          setChipIndex(i);
          setVisitedTabs((prev) => new Set([...prev, i]));
          dispatch(
            showSnackbar({
              message: `'${item.workTypeNm}' 작업종류 탭을 확인해 주세요.`,
              type: 'info',
            })
          );
          return false;
        }
        return true;
      },
    }),
    [selectedWorkTypeItems, visitedTabs, title, codeListKey, codeInfo, baseInfo, dispatch]
  );

  // step이 바뀌면 방문 기록 초기화
  const { activeStep } = useSelector((state) => state.common);
  useEffect(() => {
    setChipIndex(0);
    setVisitedTabs(new Set([0]));
  }, [activeStep]);

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      style={{ paddingBottom: '20px' }}
    >
      {/* 상단 탭 (작업종류) */}
      {selectedWorkTypeItems.length > 0 && (
        <Stack sx={{ py: 1.5, flexShrink: 0 }} direction="row">
          <Swiper
            slidesPerView="auto"
            spaceBetween={8}
            style={{ width: '100%' }}
          >
            {selectedWorkTypeItems.map((item, index) => (
              <SwiperSlide key={item.workType} style={{ width: 'auto' }}>
                <Chip
                  sx={{
                    borderRadius: 2.5,
                    px: 0.5,
                    height: '34px',
                    backgroundColor:
                      chipIndex === index ? '#444B53' : '#F1F3F5',
                    color: chipIndex === index ? '#FFF' : '#666',
                    fontWeight: 'bold',
                    fontSize: '13px',
                  }}
                  label={item.workTypeNm}
                  onClick={() => setChipIndex(index)}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </Stack>
      )}

      {title !== '기타 특이사항' && (
        <div style={{ marginTop: '8px', marginBottom: '16px' }}>
          <p
            style={{ fontSize: '14px', color: '#939393', margin: '4px 0 0 0' }}
          >
            해당하는 {title}을 모두 선택해 주세요.
          </p>
        </div>
      )}

      <div
        style={{ display: 'flex', flexDirection: 'column', minHeight: '200px' }}
      >
        {title !== '기타 특이사항' ? (
          /* 체크리스트 모드 (위험/보호구/준비) */
          <AnimatePresence mode="wait">
            <motion.div
              key={currentWorkType}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
            >
              {filteredList.length > 0 ? (
                filteredList.map((item) => {
                  const checked = item.itemCheck === 'Y';
                  return (
                    <motion.div
                      key={item[codeKey]}
                      whileTap={{ scale: 0.98 }}
                      style={getCardStyle(checked)}
                      onClick={() => handleToggle(item[codeKey])}
                    >
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                          fontSize: '16px',
                          color: checked ? '#4f7cf7ff' : '#444',
                          flex: 1,
                          pr: 1,
                        }}
                      >
                        {item[nameKey]}
                      </Typography>
                      {checked ? (
                        <CheckCircleIcon
                          sx={{ color: '#3d60faff', fontSize: '26px' }}
                        />
                      ) : (
                        <RadioButtonUncheckedIcon
                          sx={{ color: '#C7C7C7', fontSize: '24px' }}
                        />
                      )}
                    </motion.div>
                  );
                })
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '60px 0',
                    color: '#BBB',
                  }}
                >
                  <Typography sx={{ fontSize: '15px' }}>
                    등록된 {title} 항목이 없습니다.
                  </Typography>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        ) : (
          <div style={{ marginTop: '10px' }}>
            <CustomTextAreaBox
              label={`기타 특이사항 (${currentItem?.workTypeNm || ''})`}
              rows="5"
              minHeight="150px"
              icon={Icon.Notes}
              iconColor="text-primary"
              value={baseInfo.etcRemarks?.[currentWorkItemId] || ''}
              onChange={(v) => {
                const updatedRemarks = {
                  ...(baseInfo.etcRemarks || {}),
                  [currentWorkItemId]: v,
                };
                handleChange('etcRemarks', updatedRemarks);
              }}
              placeholder={`${currentItem?.workTypeNm || '해당 작업'}에 대한 특이사항을 입력해 주세요.`}
              error={errors?.etcRemarks?.[currentWorkItemId]}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
});

export default WriteCheckListCommon;
